import { Icon } from "@legal-500/legal500-design-system";
import clsx from "clsx";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resolveEnvBadge } from "./environment";
import { SidebarIcon } from "./icons";
import { isNavGroup, type NavChild, type NavSection, type NavZone } from "./navItems";
import { CONTEXT_SEPARATOR, filterNav, flattenNav, type NavSearchHit } from "./search";
import styles from "./Sidebar.module.css";
import { useIsMobileViewport } from "./useIsMobileViewport";
import { useFavourites } from "./useFavourites";
import { useSidebarCollapsed } from "./useSidebarCollapsed";

const NAV_ID = "staff-sidebar-nav";
const SEARCH_INPUT_ID = "staff-sidebar-search";
const RESULTS_ID = "staff-sidebar-search-results";

type SidebarProps = {
  zones: NavZone[];
  /** The route the nav should show as current. */
  activeItem: string;
  onNavigate: (path: string) => void;
  /**
   * Rendered in the sidebar footer — the existing UserMenu, theme toggle and all.
   * Receives the collapsed state so the footer can shrink to fit the icon rail.
   */
  userMenuSlot?: (state: { collapsed: boolean }) => ReactNode;
  /** Raw DD_RUM_ENV value; resolved into the badge here. */
  environment?: string;
};

/** Home must match exactly, or its `/` prefix would claim every route. */
function matchesPath(activeItem: string, path: string): boolean {
  if (path === "/") return activeItem === "/";
  return activeItem === path || activeItem.startsWith(`${path}/`);
}

/**
 * The one destination the current route belongs to: the longest path that
 * matches. Prefix matching alone would light up both `/submissions` and
 * `/submissions/referees` on the referees page.
 */
function resolveActivePath(activeItem: string, paths: string[]): string | null {
  let best: string | null = null;
  for (const path of paths) {
    if (matchesPath(activeItem, path) && (best === null || path.length > best.length)) {
      best = path;
    }
  }
  return best;
}

function childMatches(activePath: string | null, child: NavChild): boolean {
  if (isNavGroup(child)) {
    return child.items.some(item => item.path === activePath);
  }
  return child.path === activePath;
}

function sectionMatches(activePath: string | null, section: NavSection): boolean {
  if (section.path) return section.path === activePath;
  return (section.children ?? []).some(child => childMatches(activePath, child));
}

export function Sidebar({
  zones,
  activeItem,
  onNavigate,
  userMenuSlot,
  environment,
}: SidebarProps) {
  const { collapsed: collapsedPreference, toggle: toggleCollapsed, expand } = useSidebarCollapsed();
  const isMobile = useIsMobileViewport();
  // The rail only collapses on desktop; on mobile the hamburger governs instead.
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  // An input is unusable at rail width, so search forces the rail open — but
  // only for as long as it is open. The persisted preference is untouched, so
  // a rail that was collapsed goes back to collapsed when search closes.
  const collapsed = collapsedPreference && !isMobile && !searchOpen;
  const env = useMemo(() => resolveEnvBadge(environment), [environment]);

  // Built from the gated zones, so results can only contain what is already visible.
  const searchIndex = useMemo(() => flattenNav(zones), [zones]);
  const destinationsByPath = useMemo(
    () => new Map(searchIndex.map(hit => [hit.path, hit])),
    [searchIndex],
  );
  const activePath = useMemo(
    () =>
      resolveActivePath(
        activeItem,
        searchIndex.map(hit => hit.path),
      ),
    [activeItem, searchIndex],
  );

  const { favouritePaths, toggleFavourite } = useFavourites();
  const favouriteSet = useMemo(() => new Set(favouritePaths), [favouritePaths]);
  /*
   * Resolved against the gated nav on every render, never trusted from storage:
   * a path that no longer exists, or that this user has since lost the grant
   * for, simply resolves to nothing and drops out of the group.
   */
  const favourites = useMemo(
    () =>
      favouritePaths
        .map(path => destinationsByPath.get(path))
        .filter((hit): hit is NavSearchHit => hit !== undefined),
    [favouritePaths, destinationsByPath],
  );
  const trimmedQuery = query.trim();
  const results = useMemo(() => {
    if (trimmedQuery === "") return [];
    const matches = filterNav(searchIndex, trimmedQuery);
    // Favourites first, match order preserved within each group — a stable
    // partition, not a re-rank, so the rest of the list does not shuffle.
    return [
      ...matches.filter(hit => favouriteSet.has(hit.path)),
      ...matches.filter(hit => !favouriteSet.has(hit.path)),
    ];
  }, [searchIndex, trimmedQuery, favouriteSet]);
  const showingResults = searchOpen && trimmedQuery !== "";

  // The section holding the current route, so it can open itself.
  const activeSectionId = useMemo(() => {
    for (const zone of zones) {
      for (const section of zone.sections) {
        if (section.children && sectionMatches(activePath, section)) return section.id;
      }
    }
    return null;
  }, [zones, activePath]);

  const [openIds, setOpenIds] = useState<string[]>(() =>
    activeSectionId ? [activeSectionId] : [],
  );

  // Opening the active section must not close whatever the operator opened by
  // hand, so this adds rather than replaces.
  useEffect(() => {
    if (!activeSectionId) return;
    setOpenIds(prev => (prev.includes(activeSectionId) ? prev : [...prev, activeSectionId]));
  }, [activeSectionId]);

  const toggleSection = useCallback(
    (id: string) => {
      // On the collapsed rail there is nowhere to show children, so the first
      // click on a section reopens the sidebar and lands on that section.
      if (collapsed) {
        expand();
        setOpenIds(prev => (prev.includes(id) ? prev : [...prev, id]));
        return;
      }
      setOpenIds(prev => (prev.includes(id) ? prev.filter(open => open !== id) : [...prev, id]));
    },
    [collapsed, expand],
  );

  const handleNavigate = useCallback(
    (event: React.MouseEvent, path: string) => {
      event.preventDefault();
      setMobileOpen(false);
      onNavigate(path);
    },
    [onNavigate],
  );

  const openSearch = useCallback(() => {
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setQuery("");
    searchButtonRef.current?.focus();
  }, []);

  // Focus follows the input in, and the button back out.
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const goToResult = useCallback(
    (hit: NavSearchHit) => {
      setMobileOpen(false);
      // Leave the menu in the state it would be in had they navigated by hand.
      setOpenIds(prev => (prev.includes(hit.sectionId) ? prev : [...prev, hit.sectionId]));
      setSearchOpen(false);
      setQuery("");
      onNavigate(hit.path);
      searchButtonRef.current?.focus();
    },
    [onNavigate],
  );

  const focusResult = useCallback((index: number) => {
    resultRefs.current[index]?.focus();
  }, []);

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeSearch();
      return;
    }
    if (event.key === "ArrowDown" && results.length > 0) {
      event.preventDefault();
      focusResult(0);
      return;
    }
    if (event.key === "Enter" && results.length > 0) {
      event.preventDefault();
      goToResult(results[0]);
    }
  };

  const onResultKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>, index: number) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeSearch();
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusResult(Math.min(index + 1, results.length - 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      // Up from the first result goes back to the query, not nowhere.
      if (index === 0) searchInputRef.current?.focus();
      else focusResult(index - 1);
    }
  };

  const renderLeafLabel = (label: string, badge?: number) => (
    <>
      <span className={clsx(styles.leafLabel, collapsed && styles.visuallyHidden)}>{label}</span>
      {badge !== undefined && badge > 0 && <span className={styles.count}>{badge}</span>}
    </>
  );

  /*
   * The star is a sibling of the row's link, never a child of it: nesting one
   * interactive element in another would make favouriting also navigate.
   * Hidden until the row is hovered or the star itself is focused, and
   * permanently visible once on, so the state is readable without hovering.
   */
  const renderStar = (path: string, label: string, onEscape?: () => void) => {
    const isOn = favouriteSet.has(path);
    const action = isOn ? `Remove ${label} from favourites` : `Add ${label} to favourites`;
    return (
      <button
        type="button"
        className={clsx(styles.star, isOn && styles.starOn)}
        aria-label={action}
        title={action}
        onClick={event => {
          event.preventDefault();
          event.stopPropagation();
          toggleFavourite(path);
        }}
        // In search results, Escape must still close from wherever focus sits.
        onKeyDown={
          onEscape
            ? event => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  onEscape();
                }
              }
            : undefined
        }
      >
        <SidebarIcon name="star" className={clsx(styles.starIcon, isOn && styles.starIconOn)} />
      </button>
    );
  };

  const renderLeafRow = (item: { path: string; label: string; badge?: number }) => (
    <div key={item.path} className={styles.leafRow}>
      <a
        href={item.path}
        className={clsx(styles.leaf, item.path === activePath && styles.current)}
        aria-current={item.path === activePath ? "page" : undefined}
        onClick={event => handleNavigate(event, item.path)}
      >
        {renderLeafLabel(item.label, item.badge)}
      </a>
      {!collapsed && renderStar(item.path, item.label)}
    </div>
  );

  const renderChild = (child: NavChild) => {
    if (isNavGroup(child)) {
      return (
        <div key={child.groupTitle} className={styles.groupBlock}>
          <div className={styles.group}>{child.groupTitle}</div>
          {child.items.map(renderLeafRow)}
        </div>
      );
    }

    return renderLeafRow(child);
  };

  const renderSection = (section: NavSection) => {
    const isOpen = openIds.includes(section.id);
    const isCurrent = sectionMatches(activePath, section);

    // A section without children is a plain link (Home, Data Marts).
    if (!section.children) {
      const path = section.path ?? "/";
      return (
        <div key={section.id} className={styles.leafRow}>
          <a
            href={path}
            className={clsx(styles.item, styles.top, isCurrent && styles.current)}
            aria-current={isCurrent ? "page" : undefined}
            title={collapsed ? section.label : undefined}
            onClick={event => handleNavigate(event, path)}
          >
            <SidebarIcon name={section.icon} className={styles.ico} />
            <span className={clsx(styles.itemLabel, collapsed && styles.visuallyHidden)}>
              {section.label}
            </span>
          </a>
          {!collapsed && renderStar(path, section.label)}
        </div>
      );
    }

    const subId = `${NAV_ID}-${section.id}`;

    return (
      <div key={section.id} className={styles.sectionBlock}>
        <button
          type="button"
          className={clsx(styles.item, styles.top, isCurrent && !isOpen && styles.currentParent)}
          aria-expanded={isOpen}
          aria-controls={subId}
          title={collapsed ? section.label : undefined}
          onClick={() => toggleSection(section.id)}
        >
          <SidebarIcon name={section.icon} className={styles.ico} />
          <span className={clsx(styles.itemLabel, collapsed && styles.visuallyHidden)}>
            {section.label}
          </span>
          {!collapsed && (
            <span className={styles.chev} aria-hidden="true">
              {"▸"}
            </span>
          )}
        </button>
        {/* The container always exists so aria-controls resolves, but the
            children only mount while the section is open. */}
        <div id={subId} className={styles.sub} hidden={!isOpen || collapsed}>
          {isOpen && !collapsed && section.children.map(renderChild)}
        </div>
      </div>
    );
  };

  return (
    <aside className={clsx(styles.rail, collapsed && styles.railCollapsed)}>
      <div className={styles.brand}>
        {/*
          Collapsed, the environment rides on the brand mark as a corner badge:
          out of the layout flow, so the expand button keeps the rail's centre
          line to itself. Expanded, it stays the text badge stakeholders asked
          us to keep.
        */}
        <span
          className={styles.markWrap}
          title={collapsed ? `Environment: ${env.title}` : undefined}
        >
          <span className={styles.mark} aria-hidden="true">
            L5
          </span>
          {collapsed && (
            <span
              className={clsx(
                styles.envDot,
                env.tone === "production" ? styles.envDotProduction : styles.envDotNonProduction,
              )}
            >
              <span className={styles.visuallyHidden}>Environment: {env.title}</span>
            </span>
          )}
        </span>
        <span className={clsx(styles.name, collapsed && styles.visuallyHidden)}>Staff</span>
        {!collapsed && (
          <span
            className={clsx(
              styles.env,
              env.tone === "production" ? styles.envProduction : styles.envNonProduction,
            )}
            title={`Environment: ${env.title}`}
          >
            <span className={styles.visuallyHidden}>Environment: </span>
            <span aria-hidden="true">{env.label}</span>
          </span>
        )}
        <button
          type="button"
          ref={searchButtonRef}
          className={clsx(styles.iconbtn, styles.searchToggle)}
          onClick={searchOpen ? closeSearch : openSearch}
          aria-expanded={searchOpen}
          aria-controls={SEARCH_INPUT_ID}
          aria-label="Search menu"
          title="Search menu"
        >
          <Icon icon="magnifier" size={{ width: 0.9, height: 0.9 }} />
        </button>
        <button
          type="button"
          className={clsx(styles.iconbtn, styles.collapseToggle)}
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-controls={NAV_ID}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <span aria-hidden="true">{collapsed ? "»" : "«"}</span>
        </button>
        <button
          type="button"
          className={clsx(styles.iconbtn, styles.mobileToggle)}
          onClick={() => setMobileOpen(open => !open)}
          aria-expanded={mobileOpen}
          aria-controls={NAV_ID}
          aria-label="Toggle navigation"
        >
          <span aria-hidden="true">{"≡"}</span>
        </button>
      </div>

      {searchOpen && (
        <div className={clsx(styles.searchRow, mobileOpen && styles.navMobileOpen)}>
          <input
            id={SEARCH_INPUT_ID}
            ref={searchInputRef}
            type="text"
            className={styles.searchInput}
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Search menu"
            aria-label="Search menu"
            aria-controls={RESULTS_ID}
            autoComplete="off"
          />
          <p className={styles.visuallyHidden} aria-live="polite">
            {showingResults
              ? `${results.length} ${results.length === 1 ? "item matches" : "items match"}`
              : ""}
          </p>
        </div>
      )}

      <nav
        id={NAV_ID}
        className={clsx(styles.nav, mobileOpen && styles.navMobileOpen)}
        aria-label="Main navigation"
      >
        {showingResults ? (
          <div id={RESULTS_ID} className={styles.results}>
            {results.length === 0 ? (
              <p className={styles.noResults}>No menu items match “{trimmedQuery}”.</p>
            ) : (
              <ul className={styles.resultList}>
                {results.map((hit, index) => (
                  <li key={hit.path} className={styles.resultRow}>
                    <a
                      href={hit.path}
                      ref={element => {
                        resultRefs.current[index] = element;
                      }}
                      className={styles.result}
                      // The label and path are separate lines visually, so they
                      // need an explicit spoken name or they run together. The
                      // "›" is replaced because screen readers read it out.
                      aria-label={
                        hit.context
                          ? `${hit.label}, in ${hit.context.split(CONTEXT_SEPARATOR).join(", ")}`
                          : hit.label
                      }
                      title={hit.context ? `${hit.label} — ${hit.context}` : hit.label}
                      onClick={event => {
                        event.preventDefault();
                        goToResult(hit);
                      }}
                      onKeyDown={event => onResultKeyDown(event, index)}
                    >
                      <span className={styles.resultLabel}>{hit.label}</span>
                      {hit.context && <span className={styles.resultContext}>{hit.context}</span>}
                    </a>
                    {renderStar(hit.path, hit.label, closeSearch)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <>
            {/*
              Pinned above the zones. Hidden entirely when empty — no heading,
              no placeholder — and hidden on the collapsed rail, where leaves
              have no icon of their own to show (see the note in the README of
              this change: borrowing the parent section's icon would put the
              same glyph in the rail twice).
            */}
            {!collapsed && favourites.length > 0 && (
              <div className={styles.favouritesBlock}>
                <div className={styles.zone}>
                  <span>Favourites</span>
                </div>
                {favourites.map(favourite => (
                  <div key={favourite.path} className={styles.leafRow}>
                    <a
                      href={favourite.path}
                      className={clsx(
                        styles.leaf,
                        styles.favourite,
                        favourite.path === activePath && styles.current,
                      )}
                      aria-current={favourite.path === activePath ? "page" : undefined}
                      title={
                        favourite.context
                          ? `${favourite.label} — ${favourite.context}`
                          : favourite.label
                      }
                      onClick={event => handleNavigate(event, favourite.path)}
                    >
                      {renderLeafLabel(favourite.label, favourite.badge)}
                    </a>
                    {renderStar(favourite.path, favourite.label)}
                  </div>
                ))}
              </div>
            )}
            {zones.map(zone =>
              zone.sections.length === 0 ? null : (
                <div key={zone.id} className={styles.zoneBlock}>
                  {zone.label && (
                    <div className={styles.zone}>
                      <span className={clsx(collapsed && styles.visuallyHidden)}>{zone.label}</span>
                    </div>
                  )}
                  {zone.sections.map(renderSection)}
                </div>
              ),
            )}
          </>
        )}
      </nav>

      {userMenuSlot && (
        <div className={clsx(styles.whoami, mobileOpen && styles.navMobileOpen)}>
          {userMenuSlot({ collapsed })}
        </div>
      )}
    </aside>
  );
}
